import { useEffect, useId, useRef, type ReactNode } from 'react'

export function ModalDialog({ title, description, onClose, children }: { title: string; description?: string; onClose: () => void; children: ReactNode }) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const onCloseRef = useRef(onClose)
  const titleId = useId()
  onCloseRef.current = onClose

  useEffect(() => {
    const prior = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); onCloseRef.current(); return }
      if (event.key !== 'Tab' || !dialogRef.current) return
      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'))
      if (!focusable.length) return
      const first = focusable[0]!
      const last = focusable[focusable.length - 1]!
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
    }
    document.addEventListener('keydown', handleKeyDown)
    window.requestAnimationFrame(() => dialogRef.current?.querySelector<HTMLElement>('input, textarea, select, button')?.focus())
    return () => { document.removeEventListener('keydown', handleKeyDown); prior?.focus() }
  }, [])

  return <div className="simple-strategy-dialog-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose() }}>
    <div className="simple-strategy-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId} ref={dialogRef}>
      <header><div><h2 id={titleId}>{title}</h2>{description ? <p>{description}</p> : null}</div><button className="simple-strategy-dialog-close" type="button" aria-label={`Close ${title}`} onClick={onClose}>×</button></header>
      {children}
    </div>
  </div>
}
